import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle, MapPin } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import BrandLogo from "@/components/BrandLogo";

function buildWhatsappLink(phone, msg) {
    const cleaned = (phone || "").replace(/[^\d+]/g, "");
    const q = encodeURIComponent(msg || "");
    return `https://wa.me/${cleaned.replace(/^\+/, "")}${q ? `?text=${q}` : ""}`;
}

export default function Header() {
    const { t } = useLang();
    const { settings } = useSettings();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const loc = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 16);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setOpen(false); }, [loc.pathname]);

    const nav = [
        { to: "/", label: t.nav.home, end: true },
        { to: "/angebote", label: t.nav.offers },
        { to: "/kategorien", label: t.nav.categories },
        { to: "/ueber-uns", label: t.nav.about },
        { to: "/karriere", label: t.nav.careers },
        { to: "/kontakt", label: t.nav.contact },
        { to: "/faq", label: t.nav.faq },
    ];

    const linkCls = ({ isActive }) =>
        `relative text-sm font-medium transition-colors ${
            isActive ? "text-brand-charcoal" : "text-brand-charcoal/70 hover:text-brand-charcoal"
        }`;

    return (
        <header
            data-testid="site-header"
            className={`sticky top-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-brand-cream/90 backdrop-blur-md border-b border-black/5"
                    : "bg-brand-cream"
            }`}
        >
            <div className="container-x flex h-[72px] items-center justify-between gap-6">
                <Link to="/" data-testid="header-logo" aria-label="SCHOCK MARKT" className="shrink-0">
                    <BrandLogo />
                </Link>

                <nav className="hidden lg:flex items-center gap-7">
                    {nav.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.end}
                            data-testid={`nav-${n.to.replace("/", "") || "home"}`}
                            className={linkCls}
                        >
                            {({ isActive }) => (
                                <span className="inline-flex flex-col items-center">
                                    {n.label}
                                    <span
                                        className={`mt-1 h-[2px] w-6 transition-all ${
                                            isActive ? "bg-brand-red" : "bg-transparent"
                                        }`}
                                    />
                                </span>
                            )}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden md:flex items-center gap-2">
                    <LanguageSwitcher tone="dark" />
                    <a
                        data-testid="header-whatsapp"
                        href={buildWhatsappLink(settings.whatsapp || "+491521407101", "Hallo SCHOCK MARKT, ich habe eine Frage.")}
                        target="_blank" rel="noreferrer noopener"
                        className="inline-flex items-center gap-2 rounded-full bg-brand-green px-4 py-2 text-xs font-semibold text-white hover:bg-brand-green-soft transition"
                        aria-label="WhatsApp"
                    >
                        <MessageCircle className="h-3.5 w-3.5" />
                        {t.cta.whatsapp}
                    </a>
                    <a
                        data-testid="header-location"
                        href="#standort"
                        className="inline-flex items-center gap-2 rounded-full bg-brand-red px-4 py-2 text-xs font-semibold text-white hover:bg-brand-red-dark transition"
                    >
                        <MapPin className="h-3.5 w-3.5" />
                        {t.cta.location}
                    </a>
                </div>

                <button
                    className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-black/10 bg-white"
                    onClick={() => setOpen((v) => !v)}
                    data-testid="mobile-menu-toggle"
                    aria-label="Menü"
                >
                    {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden border-t border-black/5 bg-brand-cream" data-testid="mobile-menu">
                    <div className="container-x py-4 flex flex-col gap-1">
                        {nav.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.end}
                                data-testid={`mobile-nav-${n.to.replace("/", "") || "home"}`}
                                className={({ isActive }) =>
                                    `rounded-lg px-3 py-2 text-sm font-medium ${
                                        isActive ? "bg-brand-green/10 text-brand-green" : "text-brand-charcoal/80"
                                    }`
                                }
                            >
                                {n.label}
                            </NavLink>
                        ))}
                        <div className="mt-3 flex items-center gap-2">
                            <LanguageSwitcher tone="dark" />
                            <a
                                href={buildWhatsappLink(settings.whatsapp)}
                                target="_blank" rel="noreferrer noopener"
                                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-brand-green px-4 py-2 text-xs font-semibold text-white"
                            >
                                <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
